// src/features/records/components/GalleryView/components/MediaLightbox/hooks/useGestures.js
import { useRef, useCallback, useState } from 'react';

/**
 * Hook for handling gestures (swipe, pinch, pan) using PointerEvents
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onSwipeLeft - Callback for left swipe
 * @param {Function} options.onSwipeRight - Callback for right swipe
 * @param {Function} options.onZoom - Callback for zoom changes
 * @param {Function} options.onPan - Callback for pan changes
 * @param {boolean} options.enabled - Whether gestures are enabled
 * @param {number} options.currentScale - Current zoom scale
 * @returns {Object} Gesture handlers and state
 */
export function useGestures({
  onSwipeLeft,
  onSwipeRight,
  onZoom,
  onPan,
  onPanEnd,
  enabled = true,
  currentScale = 1
}) {
  const [isDragging, setIsDragging] = useState(false);
  const pointersRef = useRef(new Map());
  const startDataRef = useRef(null);
  const velocityRef = useRef({ x: 0, y: 0 });
  const lastTimeRef = useRef(0);
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });
  
  // Constants
  const SWIPE_THRESHOLD = 50; // Minimum distance for swipe
  const SWIPE_VELOCITY_THRESHOLD = 0.3; // Minimum velocity for swipe
  const DOUBLE_TAP_DELAY = 300; // Max time between taps
  const DOUBLE_TAP_DISTANCE = 30; // Max distance between taps
  
  /**
   * Calculate distance between two pointers
   */
  const getPointerDistance = useCallback((p1, p2) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);
  
  /**
   * Calculate center point between two pointers
   */
  const getPointerCenter = useCallback((p1, p2) => {
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2
    };
  }, []);
  
  /**
   * Handle pointer down
   */
  const handlePointerDown = useCallback((e) => {
    if (!enabled) return;
    
    const element = e.currentTarget;
    element.setPointerCapture(e.pointerId);
    
    // Store pointer
    pointersRef.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
      startX: e.clientX,
      startY: e.clientY,
      startTime: Date.now()
    });
    
    // Check for double tap (single pointer only)
    if (pointersRef.current.size === 1) {
      const now = Date.now();
      const timeDiff = now - lastTapRef.current.time;
      const dx = e.clientX - lastTapRef.current.x;
      const dy = e.clientY - lastTapRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (timeDiff < DOUBLE_TAP_DELAY && distance < DOUBLE_TAP_DISTANCE) {
        // Double tap detected
        const newScale = currentScale === 1 ? 2 : 1;
        onZoom?.(newScale, e.clientX, e.clientY);
        lastTapRef.current = { time: 0, x: 0, y: 0 }; // Reset
      } else {
        lastTapRef.current = { time: now, x: e.clientX, y: e.clientY };
      }
    }
    
    // Initialize gesture data
    if (pointersRef.current.size === 1) {
      startDataRef.current = {
        x: e.clientX,
        y: e.clientY,
        scale: currentScale,
        time: Date.now()
      };
      setIsDragging(true);
    } else if (pointersRef.current.size === 2) {
      // Two pointers - prepare for pinch
      const pointers = Array.from(pointersRef.current.values());
      startDataRef.current = {
        distance: getPointerDistance(pointers[0], pointers[1]),
        center: getPointerCenter(pointers[0], pointers[1]),
        scale: currentScale
      };
    }
  }, [enabled, currentScale, onZoom, getPointerDistance, getPointerCenter]);
  
  /**
   * Handle pointer move
   */
  const handlePointerMove = useCallback((e) => {
    if (!enabled || !pointersRef.current.has(e.pointerId)) return;
    
    // Update pointer position
    const pointer = pointersRef.current.get(e.pointerId);
    const now = Date.now();
    const dt = now - lastTimeRef.current;
    
    if (dt > 0) {
      velocityRef.current = {
        x: (e.clientX - pointer.x) / dt,
        y: (e.clientY - pointer.y) / dt
      };
    }
    
    pointer.x = e.clientX;
    pointer.y = e.clientY;
    lastTimeRef.current = now;
    
    if (pointersRef.current.size === 1 && startDataRef.current) {
      // Single pointer - pan or swipe
      const dx = e.clientX - startDataRef.current.x;
      const dy = e.clientY - startDataRef.current.y;
      
      // Always call onPan to allow carousel swiping
      onPan?.(dx, dy);
    } else if (pointersRef.current.size === 2) {
      // Two pointers - pinch zoom
      const pointers = Array.from(pointersRef.current.values());
      const distance = getPointerDistance(pointers[0], pointers[1]);
      const center = getPointerCenter(pointers[0], pointers[1]);
      
      if (startDataRef.current && startDataRef.current.distance) {
        const scale = (distance / startDataRef.current.distance) * startDataRef.current.scale;
        onZoom?.(Math.min(Math.max(scale, 1), 4), center.x, center.y);
      }
    }
  }, [enabled, onPan, onZoom, getPointerDistance, getPointerCenter]);
  
  /**
   * Handle pointer up
   */
  const handlePointerUp = useCallback((e) => {
    if (!enabled) return;
    
    const element = e.currentTarget;
    element.releasePointerCapture(e.pointerId);
    
    const pointer = pointersRef.current.get(e.pointerId);
    
    // Handle pan end for carousel
    if (pointersRef.current.size === 1 && pointer) {
      const dx = e.clientX - pointer.startX;
      const dy = e.clientY - pointer.startY;
      const velocity = velocityRef.current.x;
      
      // Call onPanEnd if provided
      if (onPanEnd) {
        onPanEnd(dx, velocity);
      } else {
        // Fallback to swipe detection if no onPanEnd
        const distanceX = Math.abs(dx);
        const distanceY = Math.abs(dy);
        const absVelocity = Math.abs(velocity);
        
        // Ensure swipe is primarily horizontal (not diagonal)
        const isHorizontalSwipe = distanceX > distanceY * 2; // At least 2:1 ratio
        
        if (currentScale === 1 && 
            distanceX > SWIPE_THRESHOLD && 
            absVelocity > SWIPE_VELOCITY_THRESHOLD && 
            isHorizontalSwipe) {
          if (dx > 0) {
            onSwipeRight?.();
          } else {
            onSwipeLeft?.();
          }
        }
      }
    }
    
    // Remove pointer
    pointersRef.current.delete(e.pointerId);
    
    // Reset if no pointers left
    if (pointersRef.current.size === 0) {
      setIsDragging(false);
      startDataRef.current = null;
      velocityRef.current = { x: 0, y: 0 };
    }
  }, [enabled, currentScale, onSwipeLeft, onSwipeRight, onPanEnd]);
  
  /**
   * Handle pointer cancel
   */
  const handlePointerCancel = useCallback((e) => {
    if (!enabled) return;
    
    const element = e.currentTarget;
    if (pointersRef.current.has(e.pointerId)) {
      element.releasePointerCapture(e.pointerId);
    }
    
    pointersRef.current.delete(e.pointerId);
    
    if (pointersRef.current.size === 0) {
      setIsDragging(false);
      startDataRef.current = null;
      velocityRef.current = { x: 0, y: 0 };
    }
  }, [enabled]);
  
  return {
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerCancel
    },
    isDragging,
    pointerCount: pointersRef.current.size
  };
}

export default useGestures;